import { useFrame } from "@react-three/fiber";
import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

function makeCloudTexture() {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  // Centre plus large, moins de blanc pur au centre
  const grd = ctx.createRadialGradient(size/2, size/2, size/3, size/2, size/2, size/2);
  grd.addColorStop(0, "rgba(220,220,240,0.09)");  // presque pas blanc
  grd.addColorStop(0.6, "rgba(200,200,220,0.05)");
  grd.addColorStop(1, "rgba(180,180,200,0.01)");  
  ctx.fillStyle = grd;
  ctx.fillRect(0,0,size,size);
  const tex = new THREE.Texture(canvas);
  tex.needsUpdate = true;
  return tex;
}



const RocketSmoke = React.forwardRef(function RocketSmoke(
  { active = false, position, count = 200, color = "#dde0ee", opacity = 0.6, billboard = true },
  ref
) {
  const smokeRef = useRef();
  const spritesRef = useRef([]);

  // Important: Générer une nouvelle texture A CHAQUE MONTAGE
  const texture = useMemo(() => makeCloudTexture(), []);

  useEffect(() => {
    if (texture) texture.needsUpdate = true;
  }, [texture]);

  // Génère la data pour chaque "puff"
  const cloudsData = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        basePosition: [
          (Math.random() - 0.5) * 8,
          (Math.random() - 0.5) * 3,
          (Math.random() - 0.5) * 8,
        ],
        speed: 0.18 + Math.random() * 0.12,
        scale: 1.2 + Math.random() * 6.5, // + large
        oscillationFreq: 0.5 + Math.random() * 1.5,
        oscillationAmp: 0.19 + Math.random() * 0.43,
        baseRot: Math.random() * Math.PI * 2,
      })),
    [count]
  );

  useFrame(({ camera, clock }) => {
    if (!active || !smokeRef.current) return;
    const elapsed = clock.getElapsedTime();
    smokeRef.current.children.forEach((sprite, i) => {
      const data = cloudsData[i];
      const yOffset =
        data.basePosition[1] +
        elapsed * data.speed +
        Math.sin(elapsed * data.oscillationFreq + i) * data.oscillationAmp;

      sprite.position.set(
        data.basePosition[0],
        yOffset,
        data.basePosition[2]
      );

      const scale = data.scale + Math.sin(elapsed * 2 + i) * 0.21;
      sprite.scale.setScalar(scale);

      if (billboard) {
        sprite.quaternion.copy(camera.quaternion);
      } else {
        sprite.rotation.z = data.baseRot + elapsed * 0.3 + i * 0.2;
      }
    });
  });

  // Fade-in à l'activation (optionnel)
  useEffect(() => {
    if (!active) return;
    spritesRef.current.forEach((sprite) => {
      if (sprite?.material) sprite.material.opacity = 0;
    });
    let t = 0;
    const fadeIn = () => {
      t += 0.08;
      spritesRef.current.forEach((sprite) => {
        if (sprite?.material)
          sprite.material.opacity = Math.min(opacity, t);
      });
      if (t < opacity) requestAnimationFrame(fadeIn);
    };
    fadeIn();
  }, [active, opacity]);

  return (
    <group
      ref={ref}
      position={position}
      visible={active}
      renderOrder={999999}
      frustumCulled={false}
    >
      {cloudsData.map((data, i) => (
        <sprite
          key={i}
          ref={el => (spritesRef.current[i] = el)}
          position={data.basePosition}
          scale={[data.scale, data.scale, data.scale]}
          renderOrder={999999}
        >
          <spriteMaterial
            attach="material"
            map={texture}
            color={color}
            opacity={opacity}
            depthWrite={false}
            transparent
            blending={THREE.NormalBlending}
          />
        </sprite>
      ))}
    </group>
  );
});

export default RocketSmoke;

